import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Shield, Truck, RotateCcw, CreditCard } from 'lucide-react';
import api from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { updatePageSEO, seoTemplates, addProductStructuredData, addBreadcrumbStructuredData } from '../utils/seo';

// Resim URL'sini dinamik olarak oluştur
const getImageUrl = (imagePath) => {
  // Resim yolu kontrolü
  if (!imagePath || imagePath === undefined || imagePath === null) {
    return '/placeholder-image.jpg';
  }
  
  // Eğer object ise url property'sini al
  let actualPath = imagePath;
  if (typeof imagePath === 'object' && imagePath.url) {
    actualPath = imagePath.url;
  } else if (typeof imagePath === 'string') {
    actualPath = imagePath;
  } else {
    return '/placeholder-image.jpg';
  }
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (actualPath.startsWith('http://')) {
      return actualPath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (actualPath.startsWith('https://')) {
      return actualPath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (actualPath.startsWith('/')) {
      return `https://leasertech.com${actualPath}`;
    }
    return `https://leasertech.com/${actualPath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  
  // Eğer dış kaynak URL ise proxy kullan
  if (actualPath.startsWith('http://') || actualPath.startsWith('https://')) {
    const proxyUrl = `${API_BASE_URL}/proxy-image?url=${encodeURIComponent(actualPath)}`;
    return proxyUrl;
  }
  
  // Yerel resim için normal yol
  const cleanedPath = actualPath.startsWith('/') ? actualPath : `/${actualPath}`;
  const finalUrl = `${API_BASE_URL}${cleanedPath}`;
  
  return finalUrl;
};

// Skeleton Loading Component
const ProductDetailSkeleton = () => (
  <div className="min-h-screen bg-gray-50 py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb Skeleton */}
      <nav className="flex mb-4 sm:mb-8 animate-pulse">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <div className="bg-gray-200 h-4 w-16 rounded"></div>
          <div className="bg-gray-200 h-3 w-3 rounded"></div>
          <div className="bg-gray-200 h-4 w-12 rounded"></div>
          <div className="bg-gray-200 h-3 w-3 rounded"></div>
          <div className="bg-gray-200 h-4 w-32 rounded"></div>
        </div>
      </nav>

      <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
          {/* Product Images Skeleton */}
          <div className="space-y-4">
            <div className="bg-gray-200 h-96 rounded-lg"></div>
            <div className="flex space-x-2">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="bg-gray-200 h-16 w-16 rounded"></div>
              ))}
            </div>
          </div>
          
          {/* Product Info Skeleton */}
          <div className="space-y-6">
            <div>
              <div className="bg-gray-200 h-8 w-3/4 rounded mb-2"></div>
              <div className="bg-gray-200 h-6 w-1/2 rounded mb-4"></div>
              <div className="bg-gray-200 h-4 w-full rounded mb-2"></div>
              <div className="bg-gray-200 h-4 w-5/6 rounded mb-2"></div>
              <div className="bg-gray-200 h-4 w-4/6 rounded"></div>
            </div>
            
            <div className="flex space-x-4">
              <div className="bg-gray-200 h-12 w-24 rounded"></div>
              <div className="bg-gray-200 h-12 w-32 rounded"></div>
            </div>
            
            <div className="bg-gray-200 h-12 w-full rounded"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const { addToCart, loading: cartLoading } = useCart();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/products/${id}`);
        const productData = response.data.product;
        setProduct(productData);
        
          // SEO Setup for product
        if (productData) {
          const seoData = seoTemplates.productDetail(productData);
          
          // Image URL'ini düzelt
          let imageUrl = 'https://leasertech.com/logo512.png';
          if (productData.images && productData.images.length > 0) {
            const firstImage = productData.images[0];
            if (typeof firstImage === 'string') {
              imageUrl = firstImage.startsWith('http') ? firstImage : `https://leasertech.com${firstImage}`;
            } else if (typeof firstImage === 'object' && firstImage.url) {
              imageUrl = firstImage.url.startsWith('http') ? firstImage.url : `https://leasertech.com${firstImage.url}`;
            }
          }
          
          updatePageSEO(
            seoData.title,
            seoData.description,
            seoData.keywords,
            imageUrl,
            `https://leasertech.com/product/${id}`
          );
          
          // Add product structured data
          addProductStructuredData(productData);
          
          // Add breadcrumb structured data with category
          const breadcrumbs = [
            { name: 'Ana Sayfa', url: 'https://leasertech.com/' },
            { name: 'Ürünler', url: 'https://leasertech.com/products' }
          ];
          
          // Kategori varsa ekle
          if (productData.categories && productData.categories.length > 0) {
            const category = productData.categories[0];
            breadcrumbs.push({
              name: category,
              url: `https://leasertech.com/products?category=${encodeURIComponent(category)}`
            });
          }
          
          breadcrumbs.push({
            name: productData.name,
            url: `https://leasertech.com/product/${id}`
          });
          
          addBreadcrumbStructuredData(breadcrumbs);
        }
      } catch (err) {
        setError('Ürün yüklenirken hata oluştu');
        console.error('Product fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Sayfa yüklendiğinde scroll'u en üste al
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAddToCart = async () => {
    // Artık misafir kullanıcılar da sepete ekleyebilir
    if ((product.stock || 0) < quantity) {
      alert('Yeterli stok bulunmuyor!');
      return;
    }

    await addToCart(product.id, quantity);
  };

  // Touch event handlers for mobile swipe
  const handleTouchStart = (e) => {
    console.log('ProductDetail Touch Start:', {
      productId: product?.id,
      images: product?.images?.length,
      currentIndex: selectedImageIndex
    });
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 30;
    const isRightSwipe = distance < -30;

    console.log('ProductDetail Touch End:', {
      productId: product?.id,
      images: product?.images?.length,
      distance,
      isLeftSwipe,
      isRightSwipe,
      currentIndex: selectedImageIndex
    });

    if (isLeftSwipe && product.images && product.images.length > 1) {
      // Swipe left - next image
      const newIndex = selectedImageIndex === product.images.length - 1 ? 0 : selectedImageIndex + 1;
      console.log('Changing from', selectedImageIndex, 'to', newIndex);
      setSelectedImageIndex(newIndex);
    }
    
    if (isRightSwipe && product.images && product.images.length > 1) {
      // Swipe right - previous image
      const newIndex = selectedImageIndex === 0 ? product.images.length - 1 : selectedImageIndex - 1;
      console.log('Changing from', selectedImageIndex, 'to', newIndex);
      setSelectedImageIndex(newIndex);
    }
  };

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ürün Bulunamadı</h1>
            <p className="text-gray-600 mb-6">{error || 'Aradığınız ürün bulunamadı.'}</p>
            <Link
              to="/products"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              Ürünlere Dön
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex mb-4 sm:mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 sm:space-x-4 overflow-x-auto">
            <li className="flex-shrink-0">
              <Link to="/" className="text-gray-400 hover:text-gray-500 text-xs sm:text-sm">
                <span className="hidden sm:inline">Ana Sayfa</span>
                <span className="sm:hidden">Ana</span>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-3 w-3 sm:h-5 sm:w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <Link to="/products" className="ml-2 sm:ml-4 text-gray-400 hover:text-gray-500 text-xs sm:text-sm">
                  <span className="hidden sm:inline">Ürünler</span>
                  <span className="sm:hidden">Ürn.</span>
                </Link>
              </div>
            </li>
            <li className="min-w-0">
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-3 w-3 sm:h-5 sm:w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="ml-2 sm:ml-4 text-gray-500 font-medium text-xs sm:text-sm truncate max-w-[150px] sm:max-w-none">
                  {product.name}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div 
                className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative cursor-pointer"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={() => {
                  if (product.images && product.images.length > 1) {
                    const newIndex = (selectedImageIndex + 1) % product.images.length;
                    setSelectedImageIndex(newIndex);
                  }
                }}
              >
                {product.images && product.images.length > 0 ? (
                  <img
                    src={getImageUrl(product.images[selectedImageIndex])}
                    alt={`${product.name} - ${product.brand || ''} - ${product.categories?.join(', ') || ''} - LeaserTech`}
                    title={`${product.name} - ${product.brand || ''}`}
                    className="w-full h-full object-contain p-4 select-none mx-auto"
                    crossOrigin="anonymous"
                    draggable={false}
                    loading="eager"
                    onError={(e) => {
                      console.error('Ana görsel yüklenemedi:', product.name, product.images[selectedImageIndex]);
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-lg">Resim Yok</span>
                  </div>
                )}
                
                {/* Swipe indicators for mobile */}
                {product.images && product.images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {product.images.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full ${
                          selectedImageIndex === index ? 'bg-white' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail Images - Responsive design */}
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-6 lg:grid-cols-4 gap-2">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index 
                          ? 'border-orange-500 scale-105' 
                          : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={getImageUrl(image)}
                        alt={`${product.name} görsel ${index + 1} - ${product.brand || ''}`}
                        title={`${product.name} - Görsel ${index + 1}`}
                        className="w-full h-full object-contain p-1 lg:p-2 mx-auto"
                        crossOrigin="anonymous"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Thumbnail görsel yüklenemedi:', product.name, image);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
                {product.brand && (
                  <p className="text-lg text-gray-600 mb-4">Marka: {product.brand}</p>
                )}
              </div>

              {/* Puan */}
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-5 w-5 ${
                        star <= Math.round(parseFloat(product.rating_average) || 0)
                          ? 'text-orange-400 fill-current'
                          : 'text-gray-200'
                      }`}
                    />
                  ))}
                </div>
                {parseFloat(product.rating_count) > 0 ? (
                  <span className="text-sm text-gray-600">
                    {parseFloat(product.rating_average).toFixed(1)} ({product.rating_count} değerlendirme)
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">Henüz değerlendirme yok</span>
                )}
              </div>

              {/* Price */}
              <div className="space-y-2">
                <div className="flex items-center space-x-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ₺{(product.price || 0).toLocaleString()}
                  </span>
                  {product.original_price && product.original_price > (product.price || 0) && (
                    <span className="text-xl text-gray-500 line-through">
                      ₺{product.original_price.toLocaleString()}
                    </span>
                  )}
                </div>
                {product.original_price && product.original_price > (product.price || 0) && (
                  <div className="text-sm text-green-600 font-medium">
                    %{Math.round(((product.original_price - (product.price || 0)) / product.original_price) * 100)} indirim
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Açıklama</h3>
                <p className="text-gray-600 whitespace-pre-line">{product.description}</p>
              </div>

              {/* Categories */}
              {product.categories && product.categories.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Kategoriler</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.categories.map((category, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Stok Durumu:</span>
                <span className={`text-sm font-medium ${
                  (product.stock || 0) > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(product.stock || 0) > 0 ? `${product.stock} adet` : 'Stokta yok'}
                </span>
              </div>

              {/* Quantity Selector */}
              {(product.stock || 0) > 0 && (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Miktar:</span>
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-gray-600 hover:text-gray-800"
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 py-2 border-x border-gray-300 min-w-[60px] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock || 0, quantity + 1))}
                      className="px-3 py-2 text-gray-600 hover:text-gray-800"
                      disabled={quantity >= (product.stock || 0)}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {/* Güven ve politikalar */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Güvenli Alışveriş</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-orange-500 shrink-0" />
                    <span>256-bit SSL ile güvenli ödeme</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-orange-500 shrink-0" />
                    <span>{product.estimated_delivery || '3-5 iş günü'} kargo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4 text-orange-500 shrink-0" />
                    <Link to="/returns" className="text-orange-600 hover:underline">
                      14 gün iade hakkı
                    </Link>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-orange-500 shrink-0" />
                    <span>iyzico ile kredi kartı ödeme</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 pt-1 border-t border-gray-200">
                  <Link to="/shipping" className="text-orange-600 hover:underline">Kargo bilgileri</Link>
                  {' · '}
                  <Link to="/returns" className="text-orange-600 hover:underline">İade politikası</Link>
                  {' · '}
                  <Link to="/distance-selling" className="text-orange-600 hover:underline">Mesafeli satış sözleşmesi</Link>
                </p>
              </div>

              {/* Add to Cart Button */}
              <div className="pt-4">
                <button
                  onClick={handleAddToCart}
                  className={`w-full py-3 px-6 rounded-lg font-medium text-lg transition-colors ${
                    (product.stock || 0) > 0 && !cartLoading
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={(product.stock || 0) === 0 || cartLoading}
                >
                  {cartLoading ? 'Ekleniyor...' : (product.stock || 0) > 0 ? 'Sepete Ekle' : 'Stokta Yok'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;

